import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Archive, ArchiveRestore, FileText, Paperclip, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { Button, DataTable, FormField, Modal, PageHeader } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import {
  type ChequeDTO,
  type ChequeInput,
  type ChequeStatut,
  type ChequeType,
  CHEQUE_STATUTS,
  CHEQUE_STATUT_LABEL,
  changerStatutCheque,
  createCheque,
  deleteCheque,
  getSyntheseCheques,
  listCheques,
  updateCheque,
} from '@/services/cheques';
import { listChantiers } from '@/services/chantiers';
import { listComptesTresorerie } from '@/services/finance';
import { listTiers } from '@/services/tiers';
import { uploadFile } from '@/services/uploads';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';

interface ChequeForm {
  numero: string; type: ChequeType; banque: string; compte_id: string; tiers_id: string; beneficiaire_libre: string;
  chantier_id: string; montant: number; date_emission: string; date_echeance: string; statut: ChequeStatut; objet: string; notes: string;
}
const DEFAULTS: ChequeForm = { numero: '', type: 'EMIS', banque: '', compte_id: '', tiers_id: '', beneficiaire_libre: '', chantier_id: '', montant: 0, date_emission: '', date_echeance: '', statut: 'EN_ATTENTE', objet: '', notes: '' };

export function ChequesPage() {
  const queryClient = useQueryClient();
  const perms = usePerms('cheques');
  const [type, setType] = useState<ChequeType | 'TOUS'>('TOUS');
  const [statut, setStatut] = useState<ChequeStatut | ''>('');
  const [q, setQ] = useState('');
  const [archives, setArchives] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<ChequeDTO | null>(null);
  const [scan, setScan] = useState<{ url: string; filename: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  const params = { type: type === 'TOUS' ? undefined : type, statut: statut || undefined, q: q || undefined, archives, size: 200 };
  const { data, isLoading } = useQuery({ queryKey: ['cheques', params], queryFn: () => listCheques(params) });
  const { data: synth } = useQuery({ queryKey: ['cheques-synthese'], queryFn: getSyntheseCheques });
  const { data: tiers } = useQuery({ queryKey: ['tiers', 'all'], queryFn: () => listTiers({ size: 200 }), enabled: open });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers(), enabled: open });
  const { data: comptes } = useQuery({ queryKey: ['comptes-tresorerie'], queryFn: listComptesTresorerie, enabled: open });

  const { register, handleSubmit, reset, watch } = useForm<ChequeForm>({ defaultValues: DEFAULTS });
  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ['cheques'] }); queryClient.invalidateQueries({ queryKey: ['cheques-synthese'] }); };

  const openCreate = () => { setEdit(null); setScan(null); reset({ ...DEFAULTS, type: type === 'RECU' ? 'RECU' : 'EMIS' }); setOpen(true); };
  const openEdit = (c: ChequeDTO) => {
    setEdit(c); setScan(c.scan_url ? { url: c.scan_url, filename: c.scan_url.split('/').pop() || 'scan' } : null);
    reset({ numero: c.numero, type: c.type, banque: c.banque ?? '', compte_id: c.compte_id ?? '', tiers_id: c.tiers_id ?? '', beneficiaire_libre: c.beneficiaire_libre ?? '', chantier_id: c.chantier_id ?? '', montant: Number(c.montant), date_emission: c.date_emission ?? '', date_echeance: c.date_echeance ?? '', statut: c.statut, objet: c.objet ?? '', notes: c.notes ?? '' });
    setOpen(true);
  };

  const toPayload = (v: ChequeForm): ChequeInput => ({
    numero: v.numero, type: v.type, banque: v.banque || null, compte_id: v.compte_id || null,
    tiers_id: v.tiers_id || null, beneficiaire_libre: v.tiers_id ? null : (v.beneficiaire_libre || null),
    chantier_id: v.chantier_id || null, montant: Number(v.montant) || 0,
    date_emission: v.date_emission || null, date_echeance: v.date_echeance || null,
    statut: v.statut, objet: v.objet || null, notes: v.notes || null, scan_url: scan?.url ?? null,
  });
  const saveM = useMutation({
    mutationFn: (v: ChequeForm) => edit ? updateCheque(edit.id, toPayload(v)) : createCheque(toPayload(v)),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(edit ? 'Chèque modifié.' : 'Chèque enregistré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const statutM = useMutation({ mutationFn: (v: { id: string; statut: ChequeStatut }) => changerStatutCheque(v.id, v.statut), onSuccess: () => invalidate(), onError: (e) => toast.error(getApiErrorMessage(e)) });
  const archiveM = useMutation({ mutationFn: (v: { id: string; archived: boolean }) => updateCheque(v.id, { is_archived: v.archived }), onSuccess: () => { invalidate(); toast.success('Mis à jour.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const deleteM = useMutation({ mutationFn: deleteCheque, onSuccess: () => { invalidate(); toast.success('Chèque supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  const onFile = async (f: File | undefined) => { if (!f) return; setUploading(true); try { setScan(await uploadFile(f)); } catch (e) { toast.error(getApiErrorMessage(e)); } finally { setUploading(false); } };
  const tiersId = watch('tiers_id');

  const columns: ColumnDef<ChequeDTO, unknown>[] = [
    { accessorKey: 'numero', header: 'N° chèque', size: 120 },
    { accessorKey: 'type', header: 'Type', size: 80, cell: ({ getValue }) => <span className={cn('rounded px-2 py-0.5 text-xs font-medium', getValue() === 'EMIS' ? 'bg-status-stuck/10 text-status-stuck' : 'bg-status-done/10 text-status-done')}>{getValue() === 'EMIS' ? 'Émis' : 'Reçu'}</span> },
    { accessorKey: 'banque', header: 'Banque', size: 130, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'tiers_nom', header: 'Bénéficiaire / Émetteur', cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'montant', header: 'Montant', size: 120, cell: ({ getValue }) => <span className="tabular-nums font-medium">{formatDZD(getValue() as number)}</span> },
    { accessorKey: 'date_echeance', header: 'Échéance', size: 110, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    {
      accessorKey: 'statut', header: 'Statut', size: 150,
      cell: ({ row }) => (
        <select value={row.original.statut} disabled={!perms.canModifier} onChange={(e) => statutM.mutate({ id: row.original.id, statut: e.target.value as ChequeStatut })} className="h-8 rounded-md border border-border bg-surface-secondary px-1.5 text-xs disabled:opacity-60">
          {CHEQUE_STATUTS.map((s) => <option key={s} value={s}>{CHEQUE_STATUT_LABEL[s]}</option>)}
        </select>
      ),
    },
    {
      id: 'actions', header: '', size: 150,
      cell: ({ row }) => {
        const c = row.original;
        return (
          <div className="flex justify-end gap-1">
            {c.scan_url && <a href={c.scan_url} target="_blank" rel="noopener noreferrer" className="rounded p-1.5 text-brand-primary hover:bg-brand-primary/10" title="Voir le scan"><FileText className="size-4" /></a>}
            {perms.canModifier && <button onClick={() => openEdit(c)} className="rounded p-1.5 text-content-muted hover:bg-surface-hover" title="Modifier"><Pencil className="size-4" /></button>}
            {perms.canArchiver && <button onClick={() => archiveM.mutate({ id: c.id, archived: !c.is_archived })} className="rounded p-1.5 text-content-muted hover:bg-surface-hover" title={c.is_archived ? 'Restaurer' : 'Archiver'}>{c.is_archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}</button>}
            {perms.canSupprimer && <button onClick={() => { if (window.confirm('Supprimer ce chèque ?')) deleteM.mutate(c.id); }} className="rounded p-1.5 text-status-stuck hover:bg-status-stuck/10" title="Supprimer"><Trash2 className="size-4" /></button>}
          </div>
        );
      },
    },
  ];

  const SegBtn = ({ v, label }: { v: ChequeType | 'TOUS'; label: string }) => (
    <button onClick={() => setType(v)} className={cn('rounded px-3 py-1.5 text-sm font-medium transition-colors', type === v ? 'bg-brand-accent text-brand-accent-contrast' : 'text-content-secondary hover:bg-surface-hover')}>{label}</button>
  );

  return (
    <>
      <PageHeader title="Chèques" subtitle="Suivi des chèques émis et reçus, échéances, encaissements et scans" breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Chèques' }]} />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { l: 'Émis en attente', v: synth?.emis_en_cours, c: 'text-status-stuck' },
          { l: 'Reçus en attente', v: synth?.recus_en_cours, c: 'text-status-done' },
          { l: 'Émis encaissés', v: synth?.emis_encaisses, c: 'text-content-secondary' },
          { l: 'Reçus encaissés', v: synth?.recus_encaisses, c: 'text-content-secondary' },
        ].map((s) => (
          <div key={s.l} className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
            <p className="label-caps text-content-muted">{s.l}</p>
            <p className={cn('mt-1 text-xl font-bold tabular-nums', s.c)}>{formatDZD(s.v ?? 0)}</p>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-secondary p-3 shadow-sm">
        <div className="inline-flex rounded-md border border-border p-0.5"><SegBtn v="TOUS" label="Tous" /><SegBtn v="EMIS" label="Émis" /><SegBtn v="RECU" label="Reçus" /></div>
        <select value={statut} onChange={(e) => setStatut(e.target.value as ChequeStatut | '')} className="h-9 rounded-md border border-border bg-surface-secondary px-2 text-sm">
          <option value="">Tous statuts</option>{CHEQUE_STATUTS.map((s) => <option key={s} value={s}>{CHEQUE_STATUT_LABEL[s]}</option>)}
        </select>
        <Input placeholder="Rechercher (n°, banque, objet)…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-64" />
        <label className="flex items-center gap-2 text-sm text-content-secondary"><input type="checkbox" className="size-4 accent-brand-primary" checked={archives} onChange={(e) => setArchives(e.target.checked)} /> Archivés</label>
        {perms.canAjouter && <Button className="ml-auto" onClick={openCreate}><Plus /> Nouveau chèque</Button>}
      </div>

      <DataTable columns={columns} data={data?.items ?? []} loading={isLoading} pageSize={20} exportFileName="cheques" emptyMessage="Aucun chèque." />

      <Modal open={open} onOpenChange={setOpen} title={edit ? 'Modifier le chèque' : 'Nouveau chèque'} size="lg"
        footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={handleSubmit((v) => saveM.mutate(v))} loading={saveM.isPending}>{edit ? 'Enregistrer' : 'Créer'}</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Type"><select {...register('type')} className={selectCls}><option value="EMIS">Émis (sortant)</option><option value="RECU">Reçu (entrant)</option></select></FormField>
          <FormField label="N° de chèque" required><Input {...register('numero')} placeholder="ex : 1234567" /></FormField>
          <FormField label="Banque"><Input {...register('banque')} placeholder="ex : BEA, CPA…" /></FormField>
          <FormField label="Compte de trésorerie"><select {...register('compte_id')} className={selectCls}><option value="">— Aucun —</option>{comptes?.map((c) => <option key={c.id} value={c.id}>{c.libelle}</option>)}</select></FormField>
          <FormField label="Montant (DZD)" required><Input type="number" step="1" {...register('montant')} /></FormField>
          <FormField label="Tiers (bénéficiaire / émetteur)"><select {...register('tiers_id')} className={selectCls}><option value="">— Aucun / nom libre —</option>{tiers?.items.map((t) => <option key={t.id} value={t.id}>{t.raison_sociale}</option>)}</select></FormField>
          {!tiersId && <FormField label="…ou nom libre"><Input {...register('beneficiaire_libre')} placeholder="Bénéficiaire / émetteur" /></FormField>}
          <FormField label="Chantier (imputation)"><select {...register('chantier_id')} className={selectCls}><option value="">— Aucun —</option>{chantiers?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}</select></FormField>
          <FormField label="Date d'émission"><Input type="date" {...register('date_emission')} /></FormField>
          <FormField label="Date d'échéance"><Input type="date" {...register('date_echeance')} /></FormField>
          <FormField label="Statut"><select {...register('statut')} className={selectCls}>{CHEQUE_STATUTS.map((s) => <option key={s} value={s}>{CHEQUE_STATUT_LABEL[s]}</option>)}</select></FormField>
          <FormField label="Objet" className="sm:col-span-2"><Input {...register('objet')} placeholder="ex : règlement facture FA-2026-012" /></FormField>
          <FormField label="Scan du chèque (pièce jointe)" className="sm:col-span-2">
            {scan ? (
              <div className="flex items-center justify-between rounded-md border border-border bg-surface-primary px-3 py-2 text-sm">
                <span className="flex items-center gap-2 truncate text-content-primary"><Paperclip className="size-4 shrink-0" /><span className="truncate">{scan.filename}</span></span>
                <span className="flex items-center gap-1">
                  <a href={scan.url} target="_blank" rel="noopener noreferrer" className="rounded px-1.5 text-brand-primary hover:underline" title="Voir"><FileText className="size-4" /></a>
                  <button onClick={() => setScan(null)} className="rounded px-1.5 text-status-stuck" title="Retirer"><Trash2 className="size-4" /></button>
                </span>
              </div>
            ) : (
              <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-3 text-sm text-content-secondary hover:bg-surface-hover">
                <Upload className="size-4" /> {uploading ? 'Téléversement…' : 'Joindre une image / un PDF'}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              </label>
            )}
          </FormField>
          <FormField label="Notes" className="sm:col-span-2"><Input {...register('notes')} /></FormField>
        </div>
      </Modal>
    </>
  );
}
